import {
  isEquationBalanced,
  isSimplestCoefficientRatio,
  parseCompound,
  parseEquation,
} from './chemicalEquation';
import {
  ENERGY_SOURCES,
  formatSig,
  reviewed,
  type DataSource,
  type ReviewStatus,
} from './energyCalorimetry';
import { FUELS, energyPerGram, type Fuel } from './energyCalorimetrySenior';
import { checkNumericAnswer, type AnswerCheck } from './gasLaws';

export type FuelsMode = 'fossil-bio' | 'production' | 'emissions';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const FUELS_SUSTAINABILITY_TOOL_IDENTITY = {
  id: 'fuels-sustainability',
  toolKey: 'chemistry-fuels-sustainability',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const FUELS_MODES: readonly FuelsMode[] = ['fossil-bio', 'production', 'emissions'];

/**
 * CT-MODE: every mode starts at Year 11, so a Year 11 or 12 learner opens the first mode and
 * switches manually. Earlier Years also fall back to it (the tool starts at Year 11).
 */
export const FUELS_YEAR_MODES: readonly { mode: FuelsMode; fromYear: number }[] = [
  { mode: 'fossil-bio', fromYear: 11 },
];

export const FUELS_SOURCES: Record<'nist' | 'iupac' | 'processes' | 'model', DataSource> = {
  nist: ENERGY_SOURCES.nist,
  iupac: {
    label: 'IUPAC/CIAAW standard atomic weights',
    detail: 'Molar masses, for example M(CO₂) = 44.01 g mol⁻¹, from standard atomic weights.',
  },
  processes: {
    label: 'Standard senior chemistry descriptions of fuel feedstocks and processes',
    detail:
      'Feedstocks, catalysts and typical temperature and pressure ranges as taught in senior chemistry. They are typical ranges, not specifications of a real plant.',
  },
  model: {
    label: 'Airbotix stoichiometric teaching model',
    detail:
      'CO₂ per MJ is calculated from the balanced complete-combustion equation and the standard enthalpy of combustion. It counts only CO₂ released when the fuel burns.',
  },
};

export const FUELS_CONDITIONS =
  'Energy values are standard enthalpies of combustion at 25 °C and 100 kPa with liquid water as a product (higher heating value). Combustion is assumed to be complete.';

/** M(CO₂) in g mol⁻¹ from IUPAC standard atomic weights (12.011 + 2 × 15.999). */
export const CO2_MOLAR_MASS = 44.01;
/** M(C) in g mol⁻¹, used to diagnose a carbon-only answer. */
export const CARBON_MOLAR_MASS = 12.01;
const KJ_PER_MJ = 1000;
const G_PER_KG = 1000;
/** Two CO₂-per-MJ values within this fraction are "about the same". */
export const SAME_EMISSIONS_FRACTION = 0.03;
/** Learner answers within 2% of the model value are accepted (rounding to 3 s.f.). */
export const EMISSIONS_TOLERANCE = 0.02;
const REFERENCE_FUEL_ID = 'methane';

// ---------------------------------------------------------------------------
// Fuel chemistry from the reviewed energy data
// ---------------------------------------------------------------------------

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

/** Converts Unicode subscript digits to ASCII so the equation parser can read a formula. */
export function toAsciiFormula(formula: string): string {
  return formula.replace(/[₀-₉]/g, (digit) => String(SUBSCRIPT_DIGITS.indexOf(digit)));
}

/** Writes an ASCII equation with Unicode subscripts and arrows, e.g. "2C₂H₅OH + 3O₂ → …". */
export function prettyChemistry(ascii: string): string {
  return ascii
    .replace(/([A-Za-z)])(\d+)/g, (_, before: string, digits: string) =>
      `${before}${digits
        .split('')
        .map((digit) => SUBSCRIPT_DIGITS[Number(digit)])
        .join('')}`,
    )
    .replace(/<=>/g, '⇌')
    .replace(/->/g, '→');
}

export function findFuel(fuelId: string): Fuel | null {
  return FUELS.find((entry) => entry.id === fuelId) ?? null;
}

export interface FuelAtoms {
  carbon: number;
  hydrogen: number;
  oxygen: number;
}

/** Atom counts for a CₓHᵧO_z fuel. Returns null for any other element (fail closed). */
export function fuelAtoms(fuel: Fuel): FuelAtoms | null {
  const parsed = parseCompound(toAsciiFormula(fuel.formula));
  if (!parsed.compound) return null;
  const { atoms } = parsed.compound;
  if (Object.keys(atoms).some((symbol) => !['C', 'H', 'O'].includes(symbol))) return null;
  return { carbon: atoms.C ?? 0, hydrogen: atoms.H ?? 0, oxygen: atoms.O ?? 0 };
}

/** Stoichiometric CO₂ released per MJ of heat from complete combustion, g MJ⁻¹. */
export function co2GramsPerMJ(fuel: Fuel): number | null {
  const atoms = fuelAtoms(fuel);
  if (!atoms || fuel.combustionkJPerMol >= 0) return null;
  return ((atoms.carbon * CO2_MOLAR_MASS) / Math.abs(fuel.combustionkJPerMol)) * KJ_PER_MJ;
}

/** CO₂ mass (kg) from burning a fuel mass (kg) completely. */
export function co2KgFromFuelKg(fuel: Fuel, fuelKg: number): number | null {
  const atoms = fuelAtoms(fuel);
  if (!atoms || !Number.isFinite(fuelKg) || fuelKg <= 0) return null;
  return (fuelKg * atoms.carbon * CO2_MOLAR_MASS) / fuel.molarMass;
}

function greatestCommonDivisor(left: number, right: number): number {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b > 0) {
    const remainder = a % b;
    a = b;
    b = remainder;
  }
  return a;
}

const COEFFICIENT_SCALES = [1, 2, 4] as const;

/**
 * Balanced complete-combustion equation in ASCII, smallest whole-number coefficients:
 * CₓHᵧO_z + (x + y/4 − z/2) O₂ → x CO₂ + y/2 H₂O. Checked with the shared equation parser.
 */
export function combustionEquation(fuel: Fuel): string | null {
  const atoms = fuelAtoms(fuel);
  if (!atoms || atoms.hydrogen === 0) return null;
  const oxygenNeeded = atoms.carbon + atoms.hydrogen / 4 - atoms.oxygen / 2;
  const scale = COEFFICIENT_SCALES.find(
    (factor) => Number.isInteger(oxygenNeeded * factor) && Number.isInteger((atoms.hydrogen / 2) * factor),
  );
  if (!scale || oxygenNeeded <= 0) return null;
  const terms = [scale, oxygenNeeded * scale, atoms.carbon * scale, (atoms.hydrogen / 2) * scale];
  const divisor = terms.filter((value) => value > 0).reduce(greatestCommonDivisor);
  const [fuelCoef, oxygenCoef, carbonCoef, waterCoef] = terms.map((value) => value / divisor);
  const coefficient = (value: number) => (value === 1 ? '' : String(value));
  const formula = toAsciiFormula(fuel.formula);
  const products = [
    carbonCoef > 0 ? `${coefficient(carbonCoef)}CO2(g)` : null,
    `${coefficient(waterCoef)}H2O(l)`,
  ].filter(Boolean);
  const equation = `${coefficient(fuelCoef)}${formula}(${fuel.state}) + ${coefficient(oxygenCoef)}O2(g) -> ${products.join(' + ')}`;
  const parsed = parseEquation(equation);
  if (!parsed.equation || !isEquationBalanced(parsed.equation)) return null;
  if (!isSimplestCoefficientRatio(parsed.equation)) return null;
  return equation;
}

// ---------------------------------------------------------------------------
// Fossil or bio: reviewed fuel cases
// ---------------------------------------------------------------------------

export type FuelOrigin = 'fossil' | 'biomass' | 'renewable-electricity';
export type Renewability = 'renewable' | 'non-renewable';
export type EmissionsComparison = 'less' | 'about-same' | 'more';

export interface FuelCase {
  id: string;
  name: string;
  fuelId: string;
  origin: FuelOrigin;
  renewability: Renewability;
  feedstock: string;
  originNote: string;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

function fuelCase(entry: Omit<FuelCase, 'sources' | 'reviewStatus'>): FuelCase {
  return { ...entry, ...reviewed([FUELS_SOURCES.nist, FUELS_SOURCES.processes]) };
}

export const FUEL_CASES: FuelCase[] = [
  fuelCase({
    id: 'natural-gas',
    name: 'Natural gas (methane)',
    fuelId: 'methane',
    origin: 'fossil',
    renewability: 'non-renewable',
    feedstock: 'Gas extracted from underground reservoirs',
    originNote:
      'Natural gas formed from buried organisms over millions of years. It is used far faster than it forms, so it is non-renewable.',
  }),
  fuelCase({
    id: 'biogas',
    name: 'Biogas (methane)',
    fuelId: 'methane',
    origin: 'biomass',
    renewability: 'renewable',
    feedstock: 'Anaerobic digestion of manure, food waste or sewage sludge',
    originNote:
      'Biogas is mostly CH₄ with some CO₂, made by microbes from recent biomass. The CH₄ molecule is identical to natural gas; only its source differs.',
  }),
  fuelCase({
    id: 'lpg-propane',
    name: 'LPG (propane)',
    fuelId: 'propane',
    origin: 'fossil',
    renewability: 'non-renewable',
    feedstock: 'Separated from natural gas and crude oil',
    originNote: 'LPG is a by-product of natural gas processing and oil refining, so it is a fossil fuel.',
  }),
  fuelCase({
    id: 'camping-butane',
    name: 'Camping gas (butane)',
    fuelId: 'butane',
    origin: 'fossil',
    renewability: 'non-renewable',
    feedstock: 'Separated from natural gas and crude oil',
    originNote: 'Butane comes from the same fossil sources as propane.',
  }),
  fuelCase({
    id: 'petrol-octane',
    name: 'Petrol (modelled as octane)',
    fuelId: 'octane',
    origin: 'fossil',
    renewability: 'non-renewable',
    feedstock: 'Fractional distillation and cracking of crude oil',
    originNote:
      'Petrol is a mixture of hydrocarbons. Octane is used here as a single model compound for that mixture.',
  }),
  fuelCase({
    id: 'bioethanol',
    name: 'Bioethanol',
    fuelId: 'ethanol',
    origin: 'biomass',
    renewability: 'renewable',
    feedstock: 'Fermentation of sugar cane, molasses or grain',
    originNote:
      'The carbon in bioethanol was taken from the air by the crop during photosynthesis, a season or two ago.',
  }),
  fuelCase({
    id: 'methanol-natural-gas',
    name: 'Methanol from natural gas',
    fuelId: 'methanol',
    origin: 'fossil',
    renewability: 'non-renewable',
    feedstock: 'Synthesis gas (CO + H₂) made from natural gas',
    originNote:
      'Most methanol today is made from natural gas or coal. Methanol can also be made from biomass, but that is not this case.',
  }),
  fuelCase({
    id: 'grey-hydrogen',
    name: 'Hydrogen from natural gas',
    fuelId: 'hydrogen',
    origin: 'fossil',
    renewability: 'non-renewable',
    feedstock: 'Steam reforming of methane',
    originNote:
      'Steam reforming turns CH₄ and H₂O into H₂ and releases CO₂ at the plant, so the fuel depends on a fossil feedstock.',
  }),
  fuelCase({
    id: 'green-hydrogen',
    name: 'Hydrogen from solar-powered electrolysis',
    fuelId: 'hydrogen',
    origin: 'renewable-electricity',
    renewability: 'renewable',
    feedstock: 'Electrolysis of water using solar or wind electricity',
    originNote:
      'Water is split with renewable electricity. The H₂ molecule is the same as grey hydrogen; only the energy and feedstock differ.',
  }),
];

export const ORIGIN_LABELS: Record<FuelOrigin, string> = {
  fossil: 'Fossil feedstock',
  biomass: 'Recent biomass',
  'renewable-electricity': 'Water + renewable electricity',
};

export const RENEWABILITY_LABELS: Record<Renewability, string> = {
  renewable: 'Renewable',
  'non-renewable': 'Non-renewable',
};

export const COMPARISON_LABELS: Record<EmissionsComparison, string> = {
  less: 'Less CO₂ per MJ than natural gas',
  'about-same': 'About the same as natural gas',
  more: 'More CO₂ per MJ than natural gas',
};

export interface FuelProfile {
  fuel: Fuel;
  energyPerGram: number;
  co2PerMJ: number;
  equation: string;
  comparison: EmissionsComparison;
}

function referenceCo2PerMJ(): number {
  const reference = findFuel(REFERENCE_FUEL_ID);
  const value = reference ? co2GramsPerMJ(reference) : null;
  if (value === null) throw new Error('Reference fuel methane is missing from the reviewed data');
  return value;
}

export function compareWithNaturalGas(co2PerMJ: number): EmissionsComparison {
  const reference = referenceCo2PerMJ();
  const difference = (co2PerMJ - reference) / reference;
  if (Math.abs(difference) <= SAME_EMISSIONS_FRACTION) return 'about-same';
  return difference < 0 ? 'less' : 'more';
}

/** Full deterministic profile for one fuel case, or null when the data cannot be modelled. */
export function fuelProfile(entry: FuelCase): FuelProfile | null {
  const fuel = findFuel(entry.fuelId);
  if (!fuel) return null;
  const co2PerMJ = co2GramsPerMJ(fuel);
  const equation = combustionEquation(fuel);
  if (co2PerMJ === null || equation === null) return null;
  return {
    fuel,
    energyPerGram: energyPerGram(fuel),
    co2PerMJ,
    equation,
    comparison: compareWithNaturalGas(co2PerMJ),
  };
}

export interface FuelPredictionCheck {
  renewabilityCorrect: boolean;
  renewabilityFeedback: string;
  comparisonCorrect: boolean;
  comparisonFeedback: string;
}

const fmt = (value: number) => formatSig(value, 3);

export function checkFuelCasePrediction(
  entry: FuelCase,
  renewability: Renewability,
  comparison: EmissionsComparison,
): FuelPredictionCheck | null {
  const profile = fuelProfile(entry);
  if (!profile) return null;
  const reference = fmt(referenceCo2PerMJ());
  const own = fmt(profile.co2PerMJ);
  const renewabilityCorrect = renewability === entry.renewability;
  const sameMolecule = FUEL_CASES.filter(
    (other) => other.fuelId === entry.fuelId && other.id !== entry.id,
  );
  const twinNote =
    sameMolecule.length > 0
      ? ` The same molecule also appears as “${sameMolecule[0].name}”, which is ${RENEWABILITY_LABELS[sameMolecule[0].renewability].toLowerCase()}: the source, not the formula, decides.`
      : '';
  const renewabilityFeedback = renewabilityCorrect
    ? `Correct: ${RENEWABILITY_LABELS[entry.renewability].toLowerCase()}. ${entry.originNote}${twinNote}`
    : `Not this time. ${entry.name} is ${RENEWABILITY_LABELS[entry.renewability].toLowerCase()}. ${entry.originNote}${twinNote}`;

  const comparisonCorrect = comparison === profile.comparison;
  let comparisonFeedback: string;
  if (profile.co2PerMJ === 0) {
    comparisonFeedback = comparisonCorrect
      ? `Correct. H₂ contains no carbon, so burning it releases 0 g CO₂ per MJ. Any CO₂ comes from how the hydrogen was made.`
      : `H₂ contains no carbon, so burning it releases 0 g CO₂ per MJ, less than natural gas at ${reference} g MJ⁻¹. Production emissions are a separate question.`;
  } else if (comparisonCorrect) {
    comparisonFeedback = `Correct. ${entry.name} releases ${own} g CO₂ per MJ; natural gas releases ${reference} g MJ⁻¹.`;
  } else if (entry.origin === 'biomass' && comparison === 'less') {
    comparisonFeedback = `A biofuel still releases CO₂ when it burns. The exhaust gives ${own} g CO₂ per MJ, compared with ${reference} g MJ⁻¹ for natural gas. Any climate benefit comes from where the carbon came from, not from the flame.`;
  } else {
    comparisonFeedback = `${entry.name} releases ${own} g CO₂ per MJ; natural gas releases ${reference} g MJ⁻¹. Fuels with more hydrogen per carbon atom release less CO₂ for each MJ.`;
  }
  return { renewabilityCorrect, renewabilityFeedback, comparisonCorrect, comparisonFeedback };
}

// ---------------------------------------------------------------------------
// Emissions: calculation cases
// ---------------------------------------------------------------------------

export type EmissionsQuestion = 'co2-mass' | 'co2-per-mj';

export interface EmissionsCase {
  id: string;
  title: string;
  fuelId: string;
  question: EmissionsQuestion;
  /** Mass of fuel burned, kg (only for co2-mass). */
  fuelKg?: number;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

function emissionsCase(entry: Omit<EmissionsCase, 'sources' | 'reviewStatus'>): EmissionsCase {
  return { ...entry, ...reviewed([FUELS_SOURCES.nist, FUELS_SOURCES.iupac, FUELS_SOURCES.model]) };
}

export const EMISSIONS_CASES: EmissionsCase[] = [
  emissionsCase({ id: 'mass-methane', title: 'Methane, 1.00 kg', fuelId: 'methane', question: 'co2-mass', fuelKg: 1 }),
  emissionsCase({ id: 'mass-octane', title: 'Octane, 1.00 kg', fuelId: 'octane', question: 'co2-mass', fuelKg: 1 }),
  emissionsCase({ id: 'mass-ethanol', title: 'Ethanol, 1.00 kg', fuelId: 'ethanol', question: 'co2-mass', fuelKg: 1 }),
  emissionsCase({ id: 'mass-propane', title: 'Propane, 0.500 kg', fuelId: 'propane', question: 'co2-mass', fuelKg: 0.5 }),
  emissionsCase({ id: 'mass-butane', title: 'Butane, 0.250 kg', fuelId: 'butane', question: 'co2-mass', fuelKg: 0.25 }),
  emissionsCase({ id: 'mass-methanol', title: 'Methanol, 2.00 kg', fuelId: 'methanol', question: 'co2-mass', fuelKg: 2 }),
  emissionsCase({ id: 'mass-hydrogen', title: 'Hydrogen, 1.00 kg', fuelId: 'hydrogen', question: 'co2-mass', fuelKg: 1 }),
  emissionsCase({ id: 'mass-octane-tank', title: 'Octane, 5.00 kg', fuelId: 'octane', question: 'co2-mass', fuelKg: 5 }),
  emissionsCase({ id: 'mj-methane', title: 'Methane per MJ', fuelId: 'methane', question: 'co2-per-mj' }),
  emissionsCase({ id: 'mj-octane', title: 'Octane per MJ', fuelId: 'octane', question: 'co2-per-mj' }),
  emissionsCase({ id: 'mj-ethanol', title: 'Ethanol per MJ', fuelId: 'ethanol', question: 'co2-per-mj' }),
  emissionsCase({ id: 'mj-propane', title: 'Propane per MJ', fuelId: 'propane', question: 'co2-per-mj' }),
];

export const EMISSIONS_UNITS: Record<EmissionsQuestion, string> = {
  'co2-mass': 'kg CO₂',
  'co2-per-mj': 'g CO₂ per MJ',
};

export function emissionsPrompt(entry: EmissionsCase): string {
  const fuel = findFuel(entry.fuelId);
  if (!fuel) return 'This case is not available.';
  if (entry.question === 'co2-mass') {
    return `${fmt(entry.fuelKg ?? 0)} kg of ${fuel.name.toLowerCase()} (${fuel.formula}, M = ${fuel.molarMass} g mol⁻¹) burns completely. What mass of CO₂ forms, in kg?`;
  }
  return `${fuel.name} (${fuel.formula}) has ΔcH = ${fuel.combustionkJPerMol} kJ mol⁻¹. What mass of CO₂ forms for every 1 MJ of heat released, in g?`;
}

export interface EmissionsSolution {
  expected: number;
  working: string[];
}

export function solveEmissionsCase(entry: EmissionsCase): EmissionsSolution | null {
  const fuel = findFuel(entry.fuelId);
  const atoms = fuel ? fuelAtoms(fuel) : null;
  const equation = fuel ? combustionEquation(fuel) : null;
  if (!fuel || !atoms || !equation) return null;
  const ratio = `1 mol ${fuel.formula} gives ${atoms.carbon} mol CO₂ (${prettyChemistry(equation)})`;
  if (entry.question === 'co2-mass') {
    const fuelKg = entry.fuelKg ?? NaN;
    const expected = co2KgFromFuelKg(fuel, fuelKg);
    if (expected === null) return null;
    const moles = (fuelKg * G_PER_KG) / fuel.molarMass;
    return {
      expected,
      working: [
        ratio,
        `n(fuel) = ${fmt(fuelKg * G_PER_KG)} g ÷ ${fuel.molarMass} g mol⁻¹ = ${fmt(moles)} mol`,
        `n(CO₂) = ${atoms.carbon} × ${fmt(moles)} mol = ${fmt(moles * atoms.carbon)} mol`,
        `m(CO₂) = ${fmt(moles * atoms.carbon)} mol × ${CO2_MOLAR_MASS} g mol⁻¹ = ${fmt(expected * G_PER_KG)} g`,
        `m(CO₂) = ${fmt(expected * G_PER_KG)} g ÷ ${G_PER_KG} g kg⁻¹ = ${fmt(expected)} kg`,
      ],
    };
  }
  const expected = co2GramsPerMJ(fuel);
  if (expected === null) return null;
  const molesPerMJ = KJ_PER_MJ / Math.abs(fuel.combustionkJPerMol);
  return {
    expected,
    working: [
      ratio,
      `n(fuel) per MJ = ${KJ_PER_MJ} kJ ÷ ${Math.abs(fuel.combustionkJPerMol)} kJ mol⁻¹ = ${formatSig(molesPerMJ, 3)} mol`,
      `m(CO₂) = ${atoms.carbon} × ${formatSig(molesPerMJ, 3)} mol × ${CO2_MOLAR_MASS} g mol⁻¹ = ${fmt(expected)} g`,
    ],
  };
}

/** Strict number parser for learner input; Unicode minus allowed. Returns NaN when invalid. */
export function parseLearnerNumber(raw: string): number {
  const cleaned = raw.trim().replace(/−/g, '-');
  if (!/^[-+]?(\d+(\.\d*)?|\.\d+)$/.test(cleaned)) return NaN;
  return Number(cleaned);
}

export function checkEmissionsAnswer(entry: EmissionsCase, raw: string): AnswerCheck | null {
  const fuel = findFuel(entry.fuelId);
  const atoms = fuel ? fuelAtoms(fuel) : null;
  const solution = solveEmissionsCase(entry);
  if (!fuel || !atoms || !solution) return null;
  const answer = parseLearnerNumber(raw);
  const { expected } = solution;
  const unit = EMISSIONS_UNITS[entry.question];
  const correct =
    expected === 0
      ? 'Correct: 0. H₂ has no carbon atoms, so complete combustion makes only water. CO₂ from hydrogen production is counted separately.'
      : `Correct: ${fmt(expected)} ${unit}. Each mole of ${fuel.formula} has ${atoms.carbon} carbon atom${atoms.carbon === 1 ? '' : 's'}, and every carbon atom ends up in one CO₂ molecule.`;
  const misconceptions =
    entry.question === 'co2-mass'
      ? [
          {
            value: expected / Math.max(atoms.carbon, 1),
            feedback: `Use the mole ratio. One ${fuel.formula} contains ${atoms.carbon} carbon atoms, so it makes ${atoms.carbon} CO₂ molecules, not one.`,
          },
          {
            value: entry.fuelKg ?? NaN,
            feedback:
              'Mass is conserved for the whole reaction, not for the fuel alone. Oxygen from the air is added to every carbon atom, so the CO₂ is heavier than the fuel burned.',
          },
          {
            value: (expected * CARBON_MOLAR_MASS) / CO2_MOLAR_MASS,
            feedback: `That is the mass of carbon only. Multiply by M(CO₂) = ${CO2_MOLAR_MASS} g mol⁻¹, not M(C) = ${CARBON_MOLAR_MASS} g mol⁻¹.`,
          },
          { value: expected * G_PER_KG, feedback: 'That value is in grams. Divide by 1000 to give kg.' },
        ]
      : [
          {
            value: expected / KJ_PER_MJ,
            feedback: 'That is grams of CO₂ per kJ. One MJ is 1000 kJ, so multiply by 1000.',
          },
          {
            value: expected / Math.max(atoms.carbon, 1),
            feedback: `Use the mole ratio: each ${fuel.formula} makes ${atoms.carbon} CO₂.`,
          },
          {
            value: (expected * CARBON_MOLAR_MASS) / CO2_MOLAR_MASS,
            feedback: `That is the mass of carbon only. Use M(CO₂) = ${CO2_MOLAR_MASS} g mol⁻¹.`,
          },
        ];
  return checkNumericAnswer(answer, expected, misconceptions, correct, unit, EMISSIONS_TOLERANCE);
}

// ---------------------------------------------------------------------------
// Emissions: carbon-neutral claims and their evidence boundaries
// ---------------------------------------------------------------------------

export type ClaimVerdict = 'supported' | 'not-supported' | 'depends';

export const CLAIM_VERDICT_LABELS: Record<ClaimVerdict, string> = {
  supported: 'Supported by the chemistry',
  'not-supported': 'Not supported',
  depends: 'Depends on assumptions',
};

export interface CarbonClaim {
  id: string;
  claim: string;
  verdict: ClaimVerdict;
  explanation: string;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

function perMJ(fuelId: string): string {
  const fuel = findFuel(fuelId);
  const value = fuel ? co2GramsPerMJ(fuel) : null;
  if (value === null) throw new Error(`Missing reviewed fuel ${fuelId}`);
  return fmt(value);
}

function claim(entry: Omit<CarbonClaim, 'sources' | 'reviewStatus'>): CarbonClaim {
  return { ...entry, ...reviewed([FUELS_SOURCES.model, FUELS_SOURCES.processes]) };
}

export const CARBON_CLAIMS: CarbonClaim[] = [
  claim({
    id: 'bioethanol-no-co2',
    claim: 'Burning bioethanol releases no CO₂.',
    verdict: 'not-supported',
    explanation: `C₂H₅OH burns to CO₂ and H₂O like any carbon fuel: ${perMJ('ethanol')} g CO₂ per MJ at the exhaust.`,
  }),
  claim({
    id: 'bioethanol-carbon-from-air',
    claim: 'The carbon atoms in bioethanol were recently taken from the air by photosynthesis.',
    verdict: 'supported',
    explanation:
      'The crop fixed CO₂ by photosynthesis (6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂), and fermentation turned that glucose into ethanol.',
  }),
  claim({
    id: 'bioethanol-carbon-neutral',
    claim: 'Bioethanol is carbon neutral over its whole life cycle.',
    verdict: 'depends',
    explanation:
      'The burn-and-regrow cycle only balances if you ignore or offset fossil energy for farming, fertiliser, distillation and transport, and any land cleared to grow the crop. This model cannot measure those. Fermentation CO₂ is also recent carbon from the air, so it is part of the cycle.',
  }),
  claim({
    id: 'biogas-same-as-natural-gas',
    claim: 'Biogas methane releases the same CO₂ per MJ as natural gas when it burns.',
    verdict: 'supported',
    explanation: `It is the same CH₄ molecule with the same ΔcH, so both give ${perMJ('methane')} g CO₂ per MJ. The difference is where the carbon came from.`,
  }),
  claim({
    id: 'ethanol-halves-petrol',
    claim: 'Swapping petrol (octane) for ethanol halves the CO₂ per MJ at the exhaust.',
    verdict: 'not-supported',
    explanation: `Per MJ the exhaust values are almost identical: ethanol ${perMJ('ethanol')} g and octane ${perMJ('octane')} g. Ethanol releases less energy per mole, which cancels its smaller carbon count.`,
  }),
  claim({
    id: 'methane-less-than-octane',
    claim: 'Methane releases less CO₂ per MJ than octane.',
    verdict: 'supported',
    explanation: `Methane gives ${perMJ('methane')} g CO₂ per MJ and octane ${perMJ('octane')} g. CH₄ has the highest hydrogen-to-carbon ratio, and burning hydrogen atoms releases energy without making CO₂.`,
  }),
  claim({
    id: 'hydrogen-always-zero',
    claim: 'Hydrogen is a zero-emission fuel no matter how it is made.',
    verdict: 'depends',
    explanation:
      'Burning H₂ makes only water, but steam reforming of methane (CH₄ + 2H₂O → CO₂ + 4H₂ overall) releases CO₂ at the plant. Electrolysis is low-emission only when the electricity is.',
  }),
  claim({
    id: 'fossil-renewable',
    claim: 'Fossil fuels are renewable because they came from ancient plants and plankton.',
    verdict: 'not-supported',
    explanation:
      'They formed over millions of years and we use them far faster than they form. Burning them adds carbon that has been out of the atmosphere for that long.',
  }),
  claim({
    id: 'sealed-cycle',
    claim: 'If a crop regrows and absorbs the same CO₂ its fuel released, the cycle itself adds no net CO₂.',
    verdict: 'supported',
    explanation:
      'That is the carbon-neutral argument, and it is valid for the carbon atoms in the fuel. Whether the whole system is neutral is a separate life-cycle question.',
  }),
];

export function checkClaimVerdict(entry: CarbonClaim, verdict: ClaimVerdict): AnswerCheck {
  if (verdict === entry.verdict) {
    return { status: 'correct', title: 'Correct', feedback: entry.explanation };
  }
  return {
    status: 'incorrect',
    title: `Not quite: ${CLAIM_VERDICT_LABELS[entry.verdict].toLowerCase()}`,
    feedback: entry.explanation,
  };
}

export const FUELS_MODEL_BOUNDARIES: string[] = [
  'CO₂ values count only the CO₂ made when the fuel burns completely. They exclude extraction, farming, processing, transport and land-use change.',
  'Energy values are standard ΔcH with liquid water (higher heating value). Engines and burners release less useful energy, so real CO₂ per useful MJ is higher.',
  'Incomplete combustion makes CO, soot and less heat. Flames in air also make nitrogen oxides, which this model does not include.',
  'Petrol is a mixture; octane is used as a single model compound.',
  'Hydrogen is light: it has the highest energy per gram but very little energy per litre unless it is compressed or liquefied.',
  'Process conditions are typical teaching ranges, not plant specifications or a laboratory method.',
];
